import { useLoaderData, type LoaderFunctionArgs } from "react-router";
import { Link } from "react-router";

import { Blocks } from "@/components/cms/Blocks";
import { JsonLd } from "@/components/ui/JsonLd";
import { MediaImage } from "@/components/ui/MediaImage";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section } from "@/components/ui/Section";
import { VideoBlock } from "@/components/ui/VideoBlock";
import { useSettings } from "@/content-context";
import { fetchArticle, type ArticleDetail } from "@/content";
import { articleJsonLd, videoJsonLd } from "@/lib/jsonld";
import { ogImageFor } from "@/lib/page-meta";
import { buildMetadata } from "@/lib/seo";
import { parseVideoUrl } from "@/lib/video";
import { formatDate } from "@/routes/news";

/**
 * One article.
 *
 * The loader throws a real 404 for a slug that is not published, rather than
 * rendering a "not here" page with a 200 on it. A soft 404 gets indexed as a
 * real page, which is how a draft that was never meant to exist ends up in
 * search results under the organisation's name.
 */
export async function loader({ params, request }: LoaderFunctionArgs) {
  const article = await fetchArticle(String(params.slug), request.signal);
  if (!article) throw new Response("Not found", { status: 404 });
  return article;
}

export const meta = ({ data }: { data?: unknown }) => {
  const article = data as ArticleDetail | undefined;
  if (!article) return null;
  // Search and sharing overrides from the article's advanced settings, where set.
  return buildMetadata({
    title: article.metaTitle || article.title,
    description: article.metaDescription || article.excerpt || `${article.title}, from HISCUS Nepal.`,
    path: `/news/${article.slug}`,
    type: "article",
    publishedTime: article.isoDate,
    canonical: article.canonicalUrl || undefined,
    noindex: Boolean(article.noindex),
    image: ogImageFor(article.socialMedia) ?? ogImageFor(article.media),
  });
};

export function ArticlePage() {
  const article = useLoaderData<ArticleDetail>();
  const settings = useSettings();
  const video = parseVideoUrl(article.videoUrl);

  return (
    <>
      <PageHeader
        eyebrow={article.category}
        title={article.title}
        lead={article.excerpt}
        trail={[
          { name: "Home", href: "/" },
          { name: "News", href: "/news" },
          { name: article.title, href: `/news/${article.slug}` },
        ]}
      />

      <Section>
        <div className="grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_18rem] lg:gap-16">
          <Reveal className="max-w-3xl">
            {article.media ? (
              <figure className="mb-10">
                <MediaImage
                  item={article.media}
                  sizes="(min-width: 1024px) 720px, 100vw"
                  className="w-full rounded-[20px]"
                  style={{ height: "auto" }}
                />
                {article.media.caption ? (
                  <figcaption className="mt-3 text-[0.875rem] text-muted">
                    {article.media.caption}
                  </figcaption>
                ) : null}
              </figure>
            ) : null}

            {article.videoUrl ? (
              <div className="mb-10">
                <VideoBlock url={article.videoUrl} poster={article.media} title={article.title} radius={20} />
              </div>
            ) : null}

            <Blocks blocks={article.body} />

            {article.galleryMedia.length ? (
              <RevealGroup as="ul" className="mt-12 grid gap-5 sm:grid-cols-2">
                {article.galleryMedia.map((item) => (
                  <Reveal as="li" key={item.id}>
                    <figure>
                      <MediaImage
                        item={item}
                        sizes="(min-width: 640px) 45vw, 100vw"
                        className="w-full rounded-[16px]"
                        style={{ height: "auto" }}
                      />
                      {item.caption ? (
                        <figcaption className="mt-2.5 text-[0.875rem] text-muted">{item.caption}</figcaption>
                      ) : null}
                    </figure>
                  </Reveal>
                ))}
              </RevealGroup>
            ) : null}
          </Reveal>

          <Reveal as="aside" className="lg:sticky lg:top-28">
            <dl className="space-y-3.5 text-[0.9375rem]">
              <div className="border-b border-line pb-3.5">
                <dt className="text-muted">Published</dt>
                <dd className="mt-0.5 text-ink">
                  <time dateTime={article.isoDate}>
                    {article.displayDate || formatDate(article.isoDate)}
                  </time>
                </dd>
              </div>
              {article.authorName ? (
                <div className="border-b border-line pb-3.5">
                  <dt className="text-muted">Written by</dt>
                  <dd className="mt-0.5 text-ink">
                    {article.authorName}
                    {article.authorRole ? `, ${article.authorRole}` : ""}
                  </dd>
                </div>
              ) : null}
              {article.reportingPeriod ? (
                <div className="border-b border-line pb-3.5">
                  <dt className="text-muted">Reporting period</dt>
                  <dd className="mt-0.5 text-ink">{article.reportingPeriod}</dd>
                </div>
              ) : null}
              {article.sourceUrl ? (
                <div className="border-b border-line pb-3.5">
                  <dt className="text-muted">Source</dt>
                  <dd className="mt-0.5">
                    <a
                      href={article.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-navy underline underline-offset-4"
                    >
                      Original page
                    </a>
                  </dd>
                </div>
              ) : null}
              {article.tags.length ? (
                <div>
                  <dt className="text-muted">Tags</dt>
                  <dd className="mt-1.5 flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-line bg-mist px-3 py-1 text-[0.8125rem] text-ink"
                      >
                        {tag}
                      </span>
                    ))}
                  </dd>
                </div>
              ) : null}
            </dl>

            {article.related.length ? (
              <div className="mt-10">
                <h2 className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-blue">
                  Related
                </h2>
                <ul className="mt-4 space-y-3">
                  {article.related.map((related) => (
                    <li key={related.slug}>
                      <Link
                        to={`/news/${related.slug}`}
                        className="link-underline text-[0.9375rem] font-semibold text-navy"
                      >
                        {related.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Reveal>
        </div>
      </Section>

      {/*
        Article data uses the real author and the real dates, or names the
        organisation as the author when no person is credited. It never invents
        a byline, because a byline is a claim about a person.
      */}
      <JsonLd
        data={articleJsonLd({
          title: article.title,
          description: article.excerpt,
          path: `/news/${article.slug}`,
          isoDate: article.isoDate,
          modifiedIso: article.updatedAt,
          authorName: article.authorName,
          type: article.schemaType ?? "Article",
          imageUrl: article.media?.kind === "upload" ? article.media.url : undefined,
          organisationName: settings.legalName,
        })}
      />

      {/* VideoObject only where a real, parseable video exists. */}
      {video ? (
        <JsonLd
          data={videoJsonLd({
            name: article.title,
            description: article.excerpt,
            uploadDate: article.isoDate,
            embedUrl: video.embedUrl,
            watchUrl: video.watchUrl,
          })}
        />
      ) : null}
    </>
  );
}
